import { useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player/vimeo";
import { services } from "../../../services";
import {
  clearLessonLocalProgress,
  getResumePosition,
  saveLessonLocalProgress,
} from "../utils/lesson-progress.storage";
import { GlobalContext } from "../../../store";
import { UserRoles } from "../../../types/userRoles";

const PROGRESS_SYNC_INTERVAL_MS = 30_000;
const COMPLETE_SECONDS_BEFORE_END = 30;

type PlayerRef = ReactPlayer | null;

interface LessonUserStatus {
  status?: string;
  progressSeconds?: number;
}

interface LessonProgressSource {
  id: string;
  userStatus?: LessonUserStatus;
  duration?: number;
}

interface UseLessonProgressOptions {
  lessonId: string;
  lesson?: LessonProgressSource | null;
  enabled: boolean;
  onLessonUpdate?: (data: Record<string, unknown>) => void;
}

export const useLessonProgress = ({
  lessonId,
  lesson,
  enabled,
  onLessonUpdate,
}: UseLessonProgressOptions) => {
  const playerRef = useRef<PlayerRef>(null);
  const trackingRef = useRef({
    started: false,
    completed: false,
    resumed: false,
    lastPosition: 0,
  });

  const { user } = useContext(GlobalContext);
  const hasFullAccess = UserRoles.FULL_ACCESS_ROLES.includes(user?.role);

  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(0);
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);

  const isCompleted = lesson?.userStatus?.status === "COMPLETED";
  const isInProgress = lesson?.userStatus?.status === "INPROGRESS";

  const syncProgress = useCallback(
    async (progressSeconds: number) => {
      if (!enabled || isCompleted) return;

      const response = await services.courses.updateLessonProgress(
        lessonId,
        progressSeconds,
      );

      if (response?.message === "SUCCESS") {
        onLessonUpdate?.({
          userStatus: {
            ...lesson?.userStatus,
            progressSeconds,
            ...(response.data?.userStatus ?? {}),
          },
        });
      }
    },
    [enabled, isCompleted, lesson?.userStatus, lessonId, onLessonUpdate],
  );

  const startLesson = useCallback(async () => {
    if (!enabled || isCompleted || isInProgress || trackingRef.current.started)
      return;

    trackingRef.current.started = true;

    try {
      const response = await services.courses.startLesson(lessonId);
      if (response?.message === "SUCCESS") {
        onLessonUpdate?.(response.data ?? {});
      }
    } catch {
      trackingRef.current.started = false;
    }
  }, [enabled, isCompleted, isInProgress, lessonId, onLessonUpdate]);

  const completeLesson = useCallback(async () => {
    if (!enabled || isCompleted || trackingRef.current.completed) return;

    trackingRef.current.completed = true;

    try {
      const response = await services.courses.completeLesson(lessonId);
      if (response?.message === "SUCCESS") {
        clearLessonLocalProgress(lessonId);
        onLessonUpdate?.({
          userStatus: {
            ...lesson?.userStatus,
            status: "COMPLETED",
            ...(response.data?.userStatus ?? {}),
          },
          ...(response.data ?? {}),
        });
      }
    } catch {
      trackingRef.current.completed = false;
    }
  }, [enabled, isCompleted, lesson?.userStatus, lessonId, onLessonUpdate]);

  const handlePlay = useCallback(() => {
    setHasStartedPlaying(true);
    setPlaying(true);
    void startLesson();
  }, [startLesson]);

  const handlePause = useCallback(() => {
    setPlaying(false);

    const seconds = trackingRef.current.lastPosition;
    saveLessonLocalProgress(lessonId, seconds);
    void syncProgress(seconds);
  }, [lessonId, syncProgress]);

  const handleProgress = useCallback(
    ({
      played: playedRatio,
      playedSeconds: seconds,
    }: {
      played: number;
      playedSeconds: number;
    }) => {
      setPlayed(playedRatio);
      setPlayedSeconds(seconds);
      trackingRef.current.lastPosition = seconds;
      saveLessonLocalProgress(lessonId, seconds);

      const nearEnd =
        duration > COMPLETE_SECONDS_BEFORE_END &&
        seconds >= duration - COMPLETE_SECONDS_BEFORE_END;

      if (nearEnd) {
        void completeLesson();
      }
    },
    [completeLesson, duration, lessonId],
  );

  const handleDuration = useCallback(
    (seconds: number) => {
      if (lesson && lesson.duration) {
        setDuration(lesson.duration);
        return;
      } else if (lesson && !lesson.duration && hasFullAccess) {
        services.courses.updateLesson(lesson.id, { duration: seconds });
        setDuration(seconds);
      }
    },
    [lesson, hasFullAccess],
  );

  // Resume from the furthest saved position (API or localStorage)
  useEffect(() => {
    if (!duration || trackingRef.current.resumed || !playerRef.current) return;

    const resumeAt = getResumePosition(
      lessonId,
      lesson?.userStatus?.progressSeconds ?? 0,
    );

    if (resumeAt <= 0) return;

    trackingRef.current.resumed = true;
    trackingRef.current.lastPosition = resumeAt;
    playerRef.current.seekTo(resumeAt, "seconds");
    setPlayedSeconds(resumeAt);
    setPlayed(duration ? resumeAt / duration : 0);
  }, [duration, lesson?.userStatus?.progressSeconds, lessonId]);

  // Sync progress to API every 30s while playing
  useEffect(() => {
    if (!enabled || !playing || isCompleted) return;

    const intervalId = window.setInterval(() => {
      const seconds = trackingRef.current.lastPosition;
      if (seconds > 0) {
        void syncProgress(seconds);
      }
    }, PROGRESS_SYNC_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [enabled, playing, isCompleted, syncProgress]);

  // Persist position before tab close / refresh
  useEffect(() => {
    const onBeforeUnload = () => {
      saveLessonLocalProgress(lessonId, trackingRef.current.lastPosition);
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [lessonId]);

  return {
    playerRef,
    playing,
    played,
    playedSeconds,
    duration,
    hasStartedPlaying,
    setPlaying,
    handlePlay,
    handlePause,
    handleProgress,
    handleDuration,
  };
};
